// Brief 85 §4 — EisenhowerDialog, moved out of App.tsx.
//
// MOVED, NOT REWRITTEN. AT THE MOVE (85 §4, a7542198) the statement block below
// WAS byte-identical to the body of `renderEisenhowerDialog` as it stood in
// App.tsx —
//   sha256 a13b44666b2a63dde8f98dc06ce4ca48880a306de2a3fb10289ca458d0955f9b
// — indentation included, which is why it sits at the depth it had inside
// App(); only the imports, the Props type and the signature were new. 206C §7.3
// — IT IS NOT BYTE-IDENTICAL NOW, and this sentence used to say it was: every
// slice since has changed the body in its own commit (101 §3 through 206C §1 —
// `git log --follow` on this file lists them), so the sha256 proves the MOVE,
// not the present file. Do not reformat all the same: the depth still lines
// this body up against the one it came from, and against every diff since.
//
// Props are the UNION of the App-scope names the body actually read, from a
// TypeScript-checker walk rather than chosen. At the move the dialog declared
// no hook (§0 measured zero across all 21 blocks), so it moved as a component
// without any state ownership changing hands. 206B §6.4 — IT DECLARES THREE
// NOW, all above the early return and none holding app state: `useRef` and
// `useEisenRows` (206 §4 — the grid's ref and its measured rows) and
// `useTasksSectionWidth` (206C §1 — the width of a case's whole Tasks section,
// which is the card cap; its one `useState` is local to this dialog; it
// replaced 206B §2's `useCasePanelWidth`).

// BRIEF 102 §2e — `provisionalLevel` left this list with the level letter: its
// only reader was the lookup that fed it.
import { EISEN_QUADRANTS, queueScrollToTk } from "../../utils";
import { eisenLabel } from "../../EisenBadge";
// 190 §C — the card moved to ../EisenTaskCard (byte-identical; see its header),
// and with it the imports only the card read: eisenQuadrant, EisenBadge,
// DateBlock, taskUrgencyColor, dayDiff/dayCountdownLabel, LinkIcon, todayISO.
import EisenTaskCard from "../EisenTaskCard";

/* ── BRIEF 105 §5a — THE CARDS COME UP TO THE AGENDA LIST'S SIZES ───────────
   MEASURED in the brief: this dialog ran `FS.s8`–`FS.s11` while the agenda list
   runs to `FS.s13` / `s14` / `s15`. §5a: "text and icons take the Agenda list's
   sizes. Same family, same steps."
   The mapping is by ROLE, not by a blanket increment — each element takes the
   size the agenda gives the element that does its job:

     card base text   FS.s10_5 → FS.s11_5   `.event-title` and the agenda task
                                            row both render at 11.5
     task title       FS.s11   → FS.s11_5   same
     type tag         FS.s8_5  → FS.s9_5    `etTag` (App.tsx:7246) renders s9_5 at
                      1px 4px  → 2px 6px    `padding: 2px 6px`, and the agenda's
                                            task rows already agreed on it
     day countdown    FS.s9    → FS.s13     AgendaView.tsx:623
     case name        FS.s9_5  → unchanged  the agenda's case ref is ALREADY
                                            s9_5 (AgendaView.tsx:595)
     quadrant heading FS.s11   → FS.s13     one step under the agenda's month
     its count        FS.s9    → FS.s10     the agenda's day-month
     empty state      FS.s10   → FS.s11_5   it stands where a row would
     Eisenhower badge 11px     → 12px       `CaseTaskRow`'s own non-compact size

   THE DIALOG'S `<h2>` STAYS AT s16. It is the modal's title, not card text, and
   §5a is about what is inside the quadrants. Same for the reset button.

   §5b — AND THE CARDS DO NOT GROW. Measured before and after, at all four of
   this dialog's quadrant widths (745 / 581 / 445 / 363 px): a DATED card is
   48.39px either way, because 104 §A1 established that the card's height is the
   DATE BLOCK's plus padding and none of this text comes near it. Only a dateless
   card grows, 28 → 31px, and it was and remains the short one. The full table
   and the cost to the title are in the PR body. */
// BRIEF 102 §2d/§2f — one urgency colour and one day counter, both shared —
// read by the card, in ../EisenTaskCard since 190 §C.
import { useLayoutEffect, useRef, useState } from "react"; // 206 §4; 206B §2
import { FS, RAD, Z, EISEN_RETREAT } from "../../lib/tokens"; // 206B §3 — the retreat, named once
import { useEisenRows } from "../../lib/eisenRows"; // 206 §4 — the two rows, measured
import { taskIsOpen } from "../../lib/taskCounters";
import type { TFn } from "../../lib/taxonomyTypes";

export type EisenhowerDialogProps = {
  // BRIEF 102 §2e — `ITEM_LEVEL` is gone from this list. It was the union taken
  // at extraction because the body read it; §2e deleted its only reader, and a
  // prop nothing reads is not a union member, it is a lie about the contract.
  cases: any;
  /* 111 §5c — the collective cases (a cc task's itemLinks feed); 191 §C — and
     its title, for the card's matter name. */
  collectiveCases?: { id: number; title?: string; itemLinks?: unknown }[];
  currentUser: unknown;
  eisenDragId: unknown;
  eisenOpen: boolean;
  eisenResetSnap: unknown;
  headerH: number;
  /** 206E §1.1 — the frame's LEFT edge: App's `sidebarEdge` (CSS px), the right edge of whatever
   *  the sidebar occupies on screen — open, folded, the phone drawer, or 0 when it is hidden. */
  sidebarEdge: number;
  setEisenDragId: (v: unknown) => void;
  setEisenOpen: (v: unknown) => void;
  t: TFn;
  taskStatusEntries: unknown;
  tasks: any;
  tkTypes: any;
  tryNavCase: (kind: unknown, id: unknown) => boolean;
  userMatrix: any;
  userMatrixQuadrant: (t: unknown) => string;
  userMatrixReset: () => void;
  userMatrixSetQuadrant: (tkId: unknown, quadrant: unknown) => void;
  userMatrixUndoReset: () => void;
};

/* 206C §1 [RULED — Laurent: «use the case Eisenhower card, but widen it to a
   full task section width (= half app width)»] — THE WIDTH OF A CASE'S WHOLE
   TASKS SECTION ON THIS SCREEN, READ WHILE THE DRAWER IS OPEN. It replaces
   206B §2's `useCasePanelWidth`, which carried the case view's arithmetic on
   down to ONE QUADRANT'S CARD (235 at 1280 … 555 at 2560) — close to the
   Bento's 224, which is why his glance still read it as "the Bento width".
   MEASURED FIRST, IN THE REAL APP (`scripts/drive-206c-glance.js
   --part=section`, Smoke Case 09, medium): the section `.csSec-tasks` has no
   padding and no border, its matrix board fills it, and it is one column of
   `.csGrid` — 507 · 587 · 707 · 827 · 1147 at 1280 · 1440 · 1680 · 1920 ·
   2560 with the sidebar open, 80 more with it folded. (Half the WINDOW is 133
   more than the section with the sidebar open, 53 with it folded — a constant,
   the sidebar + the panel's padding + the column gap, halved; so "half the
   app width" is the section's scale and not its figure.)
   HOW IT IS READ, AND WHY NOT BY ARITHMETIC. The section is a column of the
   case grid, and the case grid's two facts live in App.css: its column gap
   (`.csGrid { gap: … 16px }`) and its ONE-column layout under 900 px
   (`@media (max-width: 899px)`), where the section is the whole panel, not
   half. Arithmetic in TypeScript would copy both — 206B's `CASE_COL_GAP` was
   the first copy (206C §7.4: now deleted, nothing reads it). So each reading
   lays out an empty case grid of its own — a PROBE: a `div.csGrid` with two
   empty columns, hidden, zero-height and absolutely placed, appended to the
   body at the width of the case panel's content box — reads its first
   column, and removes it. App.css itself answers, at every width, with
   either sidebar state and on either side of the breakpoint (a media query
   reads the viewport, which the probe shares). The case panel is `.content`,
   App's main content box — the one element carrying that class — and this
   drawer is an overlay ABOVE it, so it is there to be read whichever section
   is showing; its content box in layout px is `clientWidth` less the
   computed paddings (the app hides every scrollbar). The probe sits under the
   same root `zoom` as the panel, so its `offsetWidth` is a layout px too, the
   unit the cap is written in. A ResizeObserver on the panel re-reads it
   through a window resize, the sidebar folding and the breakpoint; the state
   lives HERE, so a resize re-renders this dialog and not App. 0 means "not
   read yet" and the list then carries no cap — the layout effect reads it
   before the first paint of an open drawer.
   206D §1 [RULED — Laurent: "use the sidebar-OPEN width all the time"] — THE
   SECTION IS READ AS IF THE SIDEBAR WERE OPEN, whatever it is doing. 206C read
   it as it stood, so the cap grew by 80 when the sidebar folded (census, real
   app, medium: 507 · 827 · 1147 open against 587 · 907 · 1227 folded at 1280 ·
   1920 · 2560, the folded cap wider than the quadrant) — and 206D §2 folds the
   sidebar whenever this drawer opens, so it would now always have read the
   folded figure. The probe above is kept; only its width changes: the panel's
   content box, plus what the sidebar takes from the row NOW, less what it
   would take OPEN. Both are read, not copied: now is the real sidebar's
   `offsetWidth` (0 when it is out of the row — the phone drawer is
   `position: fixed`); open is a second hidden probe, a bare `aside.sidebar`
   that App.css lays out at the open width (its `210px`, `max-width: 25vw`)
   and that is removed at once. The panel and the sidebar share one flex row
   (`.app`), so the sum is the same at every point of the fold's transition. */
const PROBE_CSS = "position:absolute;top:0;left:0;height:0;overflow:hidden;visibility:hidden;pointer-events:none";
function useTasksSectionWidth(open: boolean): number {
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    if (!open) return undefined;
    const doc = globalThis.document;
    const panel = doc.querySelector<HTMLElement>(".content");
    if (!panel) return undefined;
    const read = (): void => {
      const cs = globalThis.getComputedStyle(panel);
      const side = doc.querySelector<HTMLElement>("aside.sidebar");
      const sideNow = side && globalThis.getComputedStyle(side).position !== "fixed" ? side.offsetWidth : 0;
      const sideProbe = doc.createElement("aside");
      sideProbe.className = "sidebar";
      sideProbe.setAttribute("aria-hidden", "true");
      sideProbe.style.cssText = PROBE_CSS;
      doc.body.appendChild(sideProbe);
      const sideOpen = sideProbe.offsetWidth;
      sideProbe.remove();
      const probe = doc.createElement("div");
      probe.className = "csGrid";
      probe.setAttribute("aria-hidden", "true");
      probe.style.cssText = PROBE_CSS;
      probe.style.width = `${String(panel.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) + sideNow - sideOpen)}px`;
      const col = doc.createElement("div");
      probe.append(col, doc.createElement("div"));
      doc.body.appendChild(probe);
      setW(col.offsetWidth);
      probe.remove();
    };
    read();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(panel);
    return () => {
      ro.disconnect();
    };
  }, [open]);
  return w;
}

export default function EisenhowerDialog({
  cases,
  collectiveCases,
  currentUser,
  eisenDragId,
  eisenOpen,
  eisenResetSnap,
  headerH,
  sidebarEdge,
  setEisenDragId,
  setEisenOpen,
  t,
  taskStatusEntries,
  tasks,
  tkTypes,
  tryNavCase,
  userMatrix,
  userMatrixQuadrant,
  userMatrixReset,
  userMatrixSetQuadrant,
  userMatrixUndoReset,
}: EisenhowerDialogProps) {
    /* 206 §4 — THIS COMPONENT'S FIRST TWO HOOKS, and they stand ABOVE the
       early return on purpose: a hook after `if (!eisenOpen) return null` would
       run on some renders and not others (React error #310). "Declares no
       hook" was a fact about the MOVE (85 §4) and about the fact that no state
       changed hands with it; neither is disturbed by a ref and a layout effect
       that only measure — and the file header now says so (206B §6.4).
       `useEisenRows` no-ops while the ref is null, which is every render the
       dialog is shut. */
    const gridRef = useRef<HTMLDivElement | null>(null);
    useEisenRows(gridRef, 10); // the `gap: 10` the grid below declares
    /* 206B §2 — and a third, above the same early return for the same reason:
       206C §1 — the width of a case's whole Tasks section, the card cap. */
    const sectionW = useTasksSectionWidth(eisenOpen);
    if (!eisenOpen) return null;
    const myEisenTk = tasks.filter(
      (t: any) =>
        (t.assignedCoId === currentUser || !currentUser) &&
        taskIsOpen(t, taskStatusEntries),
    );
    const QUADS = EISEN_QUADRANTS;
    const tasksByQ: Record<string, any[]> = { ui: [], u: [], i: [], s: [] };
    myEisenTk.forEach((t: any) => {
      tasksByQ[userMatrixQuadrant(t)].push(t);
    });
    const onCardClick = (t: any) => {
      /* Phase 30 Stage-3 (C-1) — C85 guard: matrix cards can ride delegated
         tasks whose case sits outside the viewer's lists; the bare nav()
         dead-jumped onto a blank CV, and cc-bound cards did nothing at all. */
      if (t.caseId) {
        if (tryNavCase("ca", t.caseId)) queueScrollToTk(t.id);
      } else if (t.ccId) {
        if (tryNavCase("cc", t.ccId)) queueScrollToTk(t.id);
      }
      setEisenOpen(false);
    };
    return (
      <div
        style={{
          position: "fixed",
          top: headerH - 1,
          left: sidebarEdge, // 206E §1.1 — beside the sidebar, never over it
          right: 0,
          bottom: 0,
          background: "var(--bs)",
          zIndex: Z.pane,
          display: "flex",
          flexDirection: "column",
          padding: "20px 24px",
          overflow: "auto",
        }}
        onClick={() => setEisenOpen(false)}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 14,
            flexShrink: 0,
          }}
        >
          <h2 style={{ margin: 0, fontSize: FS.s16, color: "var(--tp)" }}>
            {t("eisen.my_matrix_title", { fallback: "My Eisenhower matrix" })}
          </h2>
          <button
            type="button"
            className="btn btn-ghost"
            style={{
              marginLeft: "auto",
              padding: "4px 10px",
              fontSize: FS.s10,
              ...(eisenResetSnap
                ? {
                    background: "rgba(251,191,36,.15)",
                    color: "var(--am)",
                    border: "1px solid rgba(251,191,36,.35)",
                  }
                : {}),
            }}
            onClick={eisenResetSnap ? userMatrixUndoReset : userMatrixReset}
          >
            {eisenResetSnap
              ? "↶ " + t("common.undo", { fallback: "Undo" })
              : "↺ " + t("eisen.reset_to_auto", { fallback: "Reset to auto" })}
          </button>
        </div>
        <div
          ref={gridRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            /* 206 §4 [RULED — Laurent, SUPERSEDING 205 §4] — THE TWO ROWS ARE
               MEASURED, NOT DECLARED, and this is the surface his words are
               about: "in My Eisenhower the drawer fills the page, so there is
               plenty of room left in every quadrant, and in such a case make
               them NOT SQUARE but all four the same height."
               205 §4's `auto auto` is what that reverses. Content-sized rows
               are right when the board is short of room and WRONG when it is
               not: `align-content` resolves to `stretch`, which shares the
               leftover EQUALLY instead of equalising the rows, so two rows
               holding 54 and 200 px of cards in this 649 px board came out at
               246 and 392 — equal growth, not equal height. It only looked
               right in 205 because the case it measured had both rows holding
               the same amount.
               The whole rule, its arithmetic and the refutation of both CSS
               candidates are in `lib/eisenRows.ts`; this value is the starting
               template the measurement replaces on the first layout. The grid
               keeps `flex: 1; minHeight: 0`, which is what makes the board's
               height definite and is therefore "the space available". */
            gridTemplateRows: "auto auto",
            gap: 10,
            minHeight: 0,
          }}
        >
          {QUADS.map((q) => (
            <div
              key={q.k}
              onDragOver={(e) => {
                e.preventDefault();
              }}
              onDrop={(e) => {
                e.preventDefault();
                const id = +e.dataTransfer.getData("text/plain");
                if (id) userMatrixSetQuadrant(id, q.k);
                setEisenDragId(null);
              }}
              style={{
                /* 190 round 8 [AO] [RULED, Laurent 2026-09-16 — everywhere the
                   matrix renders]: the quadrant's TINT stays (its identity);
                   the outline around it, the underline under its title and
                   the per-quadrant counter go (the clutter). TaskMatrix.tsx
                   drops the same three. `q.border` keeps two readers — the
                   quadrant PICKERS in TaskModal and CaseTaskRow — so it is not
                   a dead member of EISEN_QUADRANTS. */
                background: q.bg,
                borderRadius: RAD.r10,
                padding: 12,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 8,
                  paddingBottom: 6,
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    /* §5a — the quadrant heading, one step under the agenda's
                       month name. */
                    fontSize: FS.s13,
                    fontWeight: 700,
                    color: q.accent,
                    textTransform: "uppercase",
                    letterSpacing: ".5px",
                  }}
                >
                  {eisenLabel(q.k)}
                </span>
              </div>
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  /* 205 §3b [RULED — Laurent's correction: the padding BETWEEN
                     the cards too] — THE QUADRANT'S CARD LIST, 6 → 12 with
                     `CARD_PAD`. The `gap: 6` fifteen lines up is the quadrant's
                     HEADING row and is deliberately untouched. */
                  gap: 12,
                  /* 206 §3 [RULED — Laurent: "in MY eisenhowers add a
                     character's width: now it is flush with the quadrant
                     titles, it should be one character in retreat"] — THE
                     CARDS STAND ONE CHARACTER IN, AND THE HEADING DOES NOT.
                     The quadrant's `padding: 12` is the box's inset and is
                     shared by the heading row and this list, which is exactly
                     why the two were flush; the retreat therefore belongs on
                     THIS element alone, and the heading row eleven lines up is
                     untouched.
                     `1ch` AND NOT A PIXEL GUESS, as the ruling asks: `ch` is
                     the advance of "0" in THIS element's own font, so the
                     indent is literally one character of the text it indents,
                     and it follows the app's text size — which moves as a root
                     `zoom` (App.tsx:1735-1743, `--base-font-size` pinned at
                     13px), so every length in the board scales together and a
                     `ch` figure stays one character at every setting.
                     206B §3 [RULED — Laurent: "OK, but do the same in case
                     eisenhower"] — NO LONGER THE DRAWER ONLY: `TaskMatrix`'s
                     card list carries the same retreat, and the figure is one
                     name (`EISEN_RETREAT`, lib/tokens.ts) — still `1ch`.
                     206 §5 — A MARGIN AND NOT A PADDING, and the reason is the
                     cap on the next line: `box-sizing: border-box` is global
                     (App.css:194), so a `maxWidth` would have INCLUDED a left
                     padding and the cards would have come out one character
                     narrower than the Bento's. A margin sits outside the box
                     the cap measures, so the retreat is exactly one character
                     AND the card is exactly the sibling's width. Same unit,
                     same figure, same look. */
                  marginLeft: EISEN_RETREAT,
                  /* 206 §5 [RULED — Laurent: "the cards become very oversized
                     when the screen is wide — no such problem in the case
                     Eisenhower. Limit their length to the current length of the
                     original cards in the app, depending on the zoom factor
                     chosen by the user in settings"] — THE CARDS STOP
                     STRETCHING.
                     206B §2 [RULED — Laurent: «did not mention Bento, did
                     mention sibling in TASK section»] — the cap became the
                     case matrix CARD's width (one quadrant: 235 at 1280, 555
                     at 2560), which still read to him as the Bento's.
                     206C §1 [RULED — Laurent: «use the case Eisenhower card,
                     but widen it to a full task section width (= half app
                     width)»] — AND THE CAP IS NOW THE WHOLE TASKS SECTION:
                     `sectionW`, the width a case's Tasks section has on THIS
                     screen, measured off App.css by `useTasksSectionWidth`
                     above. Measured and not fixed because it is a consequence
                     of the window and the sidebar. Nothing is subtracted: the
                     cap is the section's width and the card is the list's, so
                     a card is exactly the section wide wherever the quadrant
                     has that room. WHERE IT DOES NOT — with the sidebar folded
                     the section equals this quadrant's inner width, and the
                     retreat's `1ch` comes off that — the cards simply fill
                     the quadrant: the cap is wider than the room, measured
                     and reported, not worked around (the brief's words).
                     ON THE LIST AND NOT ON THE CARD: the list is the one box
                     this file owns, and its children stretch to it, so one
                     declaration caps every card without `EisenTaskCard`
                     growing a prop for one of its two hosts. The QUADRANT
                     keeps its full width — it is the drop target, and the
                     ruling is about the cards. */
                  maxWidth: sectionW > 0 ? sectionW : undefined,
                }}
              >
                {tasksByQ[q.k].length === 0 && (
                  <p
                    style={{
                      /* §5a — it stands where a card would. */
                      fontSize: FS.s11_5,
                      color: "var(--tm)",
                      fontStyle: "italic",
                      textAlign: "center",
                      margin: "12px 0",
                    }}
                  >
                    {t("matrix.drop_tasks_here", {
                      fallback: "Drop tasks here",
                    })}
                  </p>
                )}
                {tasksByQ[q.k].map((task) => (
                  <EisenTaskCard
                    key={task.id}
                    task={task}
                    cases={cases}
                    tkTypes={tkTypes}
                    collectiveCases={collectiveCases}
                    userMatrix={userMatrix}
                    eisenDragId={eisenDragId}
                    setEisenDragId={setEisenDragId}
                    onCardClick={onCardClick}
                    t={t}
                    q={q}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
}
